import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuthValue } from './AuthContext';
import { getApiUrl } from "../config/api";
import { getAuth } from "firebase/auth";

const UserAPIContext = createContext();

export const UserAPIProvider = ({ children }) => {
  const { user } = useAuthValue();
  const [userAPIData, setUserAPIData] = useState(() => {
    try {
      const storedData = localStorage.getItem('userAPIData');
      return storedData ? JSON.parse(storedData) : null;
    } catch {
      return null;
    }
  });

  const [loading, setLoading] = useState(() => !localStorage.getItem('userAPIData'));

  const getAccessToken = useCallback(async () => {
    if (user) {
      return await user.getIdToken();
    }
    return null;
  }, [user]);

  // Função para limpar dados do usuário
  const clearUserData = useCallback(() => {
    setUserAPIData(null);
    localStorage.removeItem('userAPIData');
    setLoading(false);
  }, []);

  // Função para buscar dados do usuário via endpoint /me
  const fetchUserData = useCallback(async (forceRefresh = false) => {
    if (!user) {
      clearUserData();
      return null;
    }

    // Se já temos dados e não é um refresh forçado, não buscar novamente
    if (userAPIData && !forceRefresh) {
      setLoading(false);
      return userAPIData;
    }

    setLoading(true);
    
    try {
      const token = await getAccessToken();
      
      if (!token) {
        throw new Error('Token de acesso não disponível');
      }

      const response = await fetch(getApiUrl('auth/me'), {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Token inválido ou expirado');
        }
        throw new Error(`Erro na API: ${response.status}`);
      }

      const data = await response.json();

      const userData = {
        id: data.id,
        username: data.username,
        userRoleId: data.userRoleId,
        userRole: data.userRole || { id: data.userRoleId, name: 'Usuário' },
        name: data.name,
        email: data.email || user.email,
      };

      setUserAPIData(userData);
      localStorage.setItem('userAPIData', JSON.stringify(userData));
      setLoading(false);
      
      return userData;
    } catch (error) {
      // Se o erro for de token inválido, limpar dados locais
      if (error.message.includes('Token inválido') || error.message.includes('401')) {
        clearUserData();
      } else {
        setLoading(false);
      }
      
      throw error;
    }
  }, [user, userAPIData, getAccessToken, clearUserData]);

  useEffect(() => {
    // Quando o usuário Firebase muda
    if (user && !userAPIData) {
      // Se o usuário está logado mas não temos dados da API, buscar
      fetchUserData();
    } else if (!user && userAPIData) {
      // Se o usuário foi deslogado, limpar dados
      clearUserData();
    } else if (!user && !userAPIData) {
      // Se não há usuário e não há dados, garantir que loading seja false
      setLoading(false);
    }
  }, [user, userAPIData, fetchUserData, clearUserData]);

  return (
    <UserAPIContext.Provider value={{ 
      userAPIData, 
      loading, 
      getAccessToken,
      clearUserData,
      fetchUserData
    }}>
      {children}
    </UserAPIContext.Provider>
  );
};

export const useUserAPI = () => useContext(UserAPIContext);

export const getAccessToken = async () => {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error("Usuário não autenticado");
  return await user.getIdToken();
};